import React, { useContext, useEffect, useState } from 'react';
import * as S from './styled';
import { useSnackbar } from 'notistack';
import Form from '../../..';
import { defaultBlurLogisticOriginForm, defaultLogisticOriginForm, } from '../../../../../store/invoice/utils';
import { ExportContext } from '../../../../../provider/ExportProvider';
import { maskFieldDate, unmaskFieldDate } from '../../utils';
import { useAbility } from '../../../../Hooks/useAbility';

export default function FormLogisticOriginUpdate() {
  const {
    logisticOrigins,
    getInvoice,
    setInvoice,
    updateLogisticOrigin,
    setModal,
    getFormMessage,
    validateForm,
    filterSelectCompanies,
    selectCompanies,
    setCompanyFilterField,
    filterLogisticOrigin,
    logisticGroupItems,
    logisticGroups,
    selectedGroupId,
  } = useContext(ExportContext)!.controllers.logistic;
  const { ability } = useAbility();

  const { enqueueSnackbar } = useSnackbar();

  const getForm = <T extends FlattenKeys<LogisticOriginForm>>(field: T) =>
    getInvoice(`forms.logisticOrigins.update.${field}`);

  const setForm = <
    T extends FlattenKeys<LogisticOriginForm>,
    V extends DeepType<InvoiceState, `forms.logisticOrigins.update.${T}`>,
  >(
    field: T,
    value: V,
  ) => setInvoice(`forms.logisticOrigins.update.${field}`, value);

  const blurField = (field: keyof LogisticOriginForm) => () => {
    setInvoice(`blur.logisticOrigins.update.${field}`, true);
  };

  const clearForm = () => {
    const logisticOrigin = logisticOrigins.find(
      ({ id }) => getInvoice('forms.logisticOrigins.update').id === id,
    )!;
    setInvoice(`forms.logisticOrigins.update`, {
      id: logisticOrigin.id,
      originInvoiceNumber: logisticOrigin.originInvoiceNumber ?? '',
      quantityVolume: logisticOrigin.quantityVolume ?? 0,
      releaseDate: logisticOrigin.releaseDate ?? '',
      company: {
        id: logisticOrigin.company.id,
        name: logisticOrigin.company.name,
      },
      group: {
        id: logisticOrigin.group?.id ?? 0,
        number: logisticOrigin.group?.number ?? 0,
        name: logisticOrigin.group?.name ?? '',
      },
    });
    setInvoice(`blur.logisticOrigins.update`, defaultBlurLogisticOriginForm());
  };

  const messages = getFormMessage('logisticOrigins.update');

  const [loading, setLoading] = React.useState(false);
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateForm('logisticOrigins.update')
      .then(() => {
        setLoading(true);
        updateLogisticOrigin()
          .then(() => {
            enqueueSnackbar('Logística criada com sucesso', {
              variant: 'success',
            });
            setInvoice('forms.logisticOrigins.update', { id: 0, ...defaultLogisticOriginForm() });
            setModal('single.updateLogisticOrigin', false);
            filterLogisticOrigin();
          })
          .catch(() => {
            enqueueSnackbar('Houve uma falha ao criar a logística', {
              variant: 'error',
            });
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        console.log({ messages })
        enqueueSnackbar('Verifique a validade dos campos', {
          variant: 'error',
        });
      });
  };

  const [companyLoading, setCompanyLoading] = useState(false);
  const filterCompanies = () => {
    setCompanyLoading(true);
    filterSelectCompanies().finally(() => {
      setCompanyLoading(false);
    });
  };

  const group = getForm('group')

  useEffect(() => {
    if (!selectedGroupId && (!group.id && logisticGroups.length > 0))
      setForm('group', logisticGroups[0]);
  }, [logisticGroups, selectedGroupId]);

  return (
    <S.Form
      style={{
        maxHeight: '80vh',
      }}
      onSubmit={onSubmit}
    >
      <S.Body>
        <S.Grid>
          <Form.Input.Dropdown
            label="Companhia"
            items={selectCompanies.map(({ id, name }) => ({ id, name }))}
            value={getForm('company.name')}
            onChange={(e) => {
              setForm('company.name', e.target.value);
              setCompanyFilterField('name')(e);
            }}
            onDebouncedChange={filterCompanies}
            error={messages.company}
            onBlur={blurField('company')}
            hasClearItem
            onFocus={() => {
              setCompanyFilterField('name')({ target: { value: '' } });
            }}
            onSelected={({ id, name }) => {
              setForm('company', +id ? { id: +id, name: `${name}` } : { id: 0, name: '' });
            }}
            loading={companyLoading}
          // disabled={ability?.cannot('update', 'Invoice', 'company')}
          />
          <Form.Input.DatePicker
            label="Data Lançamento"
            value={maskFieldDate(getForm('releaseDate'))}
            onChange={(e) => setForm('releaseDate', unmaskFieldDate(e.target.value))}
            error={messages.releaseDate}
            onBlur={blurField('releaseDate')}
          />
          <Form.Input
            label="Nota Origem"
            value={getForm('originInvoiceNumber')}
            onChange={(e) => setForm('originInvoiceNumber', e.target.value)}
            error={messages.originInvoiceNumber}
            onBlur={blurField('originInvoiceNumber')}
          />
          <Form.Input
            label="Quantidade Volume"
            value={getForm('quantityVolume')}
            onChange={(e) => setForm('quantityVolume', +e.target.value || 0)}
            error={messages.quantityVolume}
            onBlur={blurField('quantityVolume')}
          />

          {ability?.can('update', 'Invoice', 'groupId') && !selectedGroupId && (logisticGroupItems.length > 1) ? (
            <Form.Input.Dropdown
              label="Grupo"
              items={logisticGroupItems}
              value={
                logisticGroupItems.find(({ id }) => id === getForm('group')?.id)?.name ??
                ''
              }
              error={messages.group}
              onBlur={blurField('group')}
              onSelected={(item) => {
                setForm('group', item.data);
              }}
              disabled={logisticGroupItems.length === 1}
            />
          ) : null}
        </S.Grid>
      </S.Body>
      <S.Actions>
        <Form.Button type="button" onClick={clearForm}>
          Limpar
        </Form.Button>
        <Form.Button loading={loading}>Atualizar</Form.Button>
      </S.Actions>
    </S.Form>
  );
}
